import { gql, useQuery } from '@apollo/client'
import AssessmentIcon from '@mui/icons-material/Assessment'
import AttachMoneyIcon from '@mui/icons-material/AttachMoney'
import EmailIcon from '@mui/icons-material/Email'
import FacebookIcon from '@mui/icons-material/Facebook'
import InstagramIcon from '@mui/icons-material/Instagram'
import TwitterIcon from '@mui/icons-material/Twitter'
import Alert from '@mui/material/Alert'
import Box from '@mui/material/Box'
import CircularProgress from '@mui/material/CircularProgress'
import Honeybadger from 'honeybadger-js'
import I18n from 'i18n-js'
import { Fragment, useEffect } from 'react'
import { useSelector } from 'react-redux'
import { makeStyles } from 'tss-react/mui'

import EcoIcon from '@src/svgs/Eco'

import { retailerIdsForCurrentBrandOrCurrentRetailerAccountSelector } from '../../stores/app/selectors'
import { currentRetailerIdSelector } from '../../stores/retailers/selectors'
import { Breadcrumbs, Breadcrumb, BreadcrumbMenu } from '../shared/Breadcrumbs'

import OrganicCampaignDetailsHeader from './OrganicCampaignDetailsHeader'
import OrganicCampaignDetailsList from './OrganicCampaignDetailsList'
import OrganicCampaignDetailsTabs from './OrganicCampaignDetailsTabs'
import reportingPaths from './reportingPaths'

const useStyles = makeStyles()((theme) => ({
  absolute: {
    position: 'absolute',
    left: 0,
    top: 0,
    right: 0,
    bottom: 0,
    // pointerEvents: 'none',
    zIndex: theme.zIndex.appBar - 1,
  },
  absoluteColor: {
    backgroundColor: theme.palette.background.paper,
    opacity: 0.5,
  },
  progressContainer: {
    textAlign: 'center',
    position: 'sticky',
    top: 0,
    paddingTop: theme.spacing(4),
  },
}))

const ORGANIC_REPORTING_DETAILS = gql`
  query ReportingChannelSharesList(
    $campaign_id: ID!
    $resourceId: ID!
    $resourceType: ResourceType!
    $localAdsResourceType: LocalAdsResourceType!
    $retailerIds: [ID!]!
  ) {
    campaign: getCampaign(
      campaignId: $campaign_id
      params: { fields: [brand] }
    ) {
      id
      logo_url
      logo_public_id
      name
      brand {
        id
        name
        logo_url
      }
      facebook: getSharesByResourceType(
        resourceId: $resourceId
        resourceType: $resourceType
        shareType: facebook_shares
        params: {
          filterParams: { campaign_id: $campaign_id }
          fields: [share_setting, share_setting__digital_assets]
        }
      ) {
        id
        post_url
        executed_at
        scheduled_for
        status
        share_setting {
          id
          name
          post_format
          responsive_share_image_url
          digital_assets {
            id
            download_url
            external_video_url
            file {
              id
              public_id
            }
          }
          child_attachments {
            id
            media {
              source_id
            }
          }
        }
      }
      twitter: getSharesByResourceType(
        resourceId: $resourceId
        resourceType: $resourceType
        shareType: twitter_shares
        params: {
          filterParams: { campaign_id: $campaign_id }
          fields: [share_setting, share_setting__digital_assets]
        }
      ) {
        id
        picture
        executed_at
        scheduled_for
        link_clicks
        tweet_url
        share_setting {
          id
          name
          post_format
          responsive_share_image_url
          digital_assets {
            id
            download_url
            external_video_url
          }
        }
      }
      instagram: getSharesByResourceType(
        resourceId: $resourceId
        resourceType: $resourceType
        shareType: instagram_shares
        params: {
          filterParams: { campaign_id: $campaign_id }
          fields: [share_setting, share_setting__digital_assets]
        }
      ) {
        id
        picture
        executed_at
        scheduled_for
        share_mode
        share_setting {
          id
          name
          post_format
          responsive_share_image_url
          digital_assets {
            id
            download_url
          }
        }
      }
      email: getSharesByResourceType(
        resourceId: $resourceId
        resourceType: $resourceType
        shareType: email_shares
        params: {
          filterParams: { campaign_id: $campaign_id }
          fields: [share_setting]
        }
      ) {
        id
        picture
        executed_at
        scheduled_for
        link_clicks
        share_setting {
          id
          name
          post_format
          responsive_share_image_url
        }
      }
      stats: getStats(params: { retailer_ids: $retailerIds }) {
        stats {
          brand_id
          data {
            campaign_id
            shares {
              share_id
              channel
              actions {
                post_reaction_like
                post_reaction_haha
                post_reaction_angry
                post_reaction_sad
                post_reaction_love
                post_reaction_wow
                clicks
                video_views
                comments
                photo_views
                posts
                rsvps
                # twitter
                retweets
                favorites
                # instagram
                engagements
                saved
              }
              clicks {
                clicks
                clicked
              }
              views {
                impressions_organic
                impressions_paid
                opened
              }
              email {
                delivered
                processed
                deferred
                bounced
                dropped
              }
              instagram {
                impressions
                saved
                reach
                engagements
              }
            }
          }
          filter_id
          retailer_id
          stat_type
          updated_at
        }
      }
      localAds: getLocalAds(
        resourceType: $localAdsResourceType
        resourceId: $resourceId
        params: {
          campaign_id: $campaign_id
          fields: [channel_object__facebook_share]
        }
      ) {
        id
        start_date
        end_date
        actual_total_amount_cents
        ad_run_id
        channel_object {
          id
          facebook_share {
            id
          }
        }
      }
    }
  }
`

const OrganicCampaignDetails = (props) => {
  const { classes, cx } = useStyles()
  const retailerId = useSelector(currentRetailerIdSelector)
  const retailerIds = useSelector(
    retailerIdsForCurrentBrandOrCurrentRetailerAccountSelector,
  )
  const retailerAccountId = useSelector(
    (state) => state.app.currentRetailerAccount,
  )
  const {
    channel: channelParam,
    retailerAccountSlug,
    brandSlug,
    campaignId,
  } = props.match.params
  const { loading, data, error } = useQuery(ORGANIC_REPORTING_DETAILS, {
    variables: {
      campaign_id: campaignId,
      resourceId: retailerId ? retailerId : retailerAccountId,
      resourceType: retailerId ? 'retailers' : 'retailer_accounts',
      localAdsResourceType: retailerId ? 'retailers' : 'retailer_accounts',
      retailerIds,
    },
  })
  const channel = channelParam || 'facebook'
  useEffect(() => {
    if (!channelParam) {
      props.history.replace(
        reportingPaths.organicCampaignDetails.build({
          retailerAccountSlug,
          campaignId,
          channel,
        }),
      )
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  if (loading) {
    return (
      <Fragment>
        <div className={cx(classes.absolute, classes.absoluteColor)} />
        <div className={classes.absolute}>
          <div className={classes.progressContainer}>
            <CircularProgress />
          </div>
        </div>
      </Fragment>
    )
  }

  if (error) {
    Honeybadger.notify(error)
    return (
      <Box boxSizing="border-box" padding="1em 2em" width="100%">
        <Alert severity="error">{I18n.t('shared.errorSendingRequest')}</Alert>
      </Box>
    )
  }

  const stats = ((data.campaign.stats.stats[0] || {}).data || {}).shares || []

  return (
    <Box boxSizing="border-box" padding="1em 2em" width="100%">
      <Breadcrumbs my={4}>
        <Breadcrumb
          Icon={AssessmentIcon}
          to={reportingPaths.index.build({
            retailerAccountSlug,
            brandSlug: 'all',
            campaignType: 'organic',
          })}
        >
          {I18n.t('nav.reporting')}
        </Breadcrumb>
        <BreadcrumbMenu
          selected={'organic'}
          items={[
            {
              Icon: EcoIcon,
              label: I18n.t('shared.organic'),
              key: 'organic',
              to: reportingPaths.index.build({
                retailerAccountSlug,
                campaignType: 'organic',
              }),
            },
            {
              Icon: AttachMoneyIcon,
              label: I18n.t('shared.premium'),
              key: 'paid',
              to: reportingPaths.index.build({
                retailerAccountSlug,
                campaignType: 'paid',
              }),
            },
          ]}
        />
        <BreadcrumbMenu
          selected={props.match.params.channel}
          items={[
            {
              Icon: FacebookIcon,
              label: I18n.t('filters.facebook'),
              key: 'facebook',
              to: 'facebook',
            },
            {
              Icon: InstagramIcon,
              label: I18n.t('filters.instagram'),
              key: 'instagram',
              to: 'instagram',
            },
            {
              Icon: TwitterIcon,
              label: I18n.t('filters.twitter'),
              key: 'twitter',
              to: 'twitter',
            },
            {
              Icon: EmailIcon,
              label: I18n.t('shared.email'),
              key: 'email',
              to: 'email',
            },
          ]}
        />
      </Breadcrumbs>
      <OrganicCampaignDetailsHeader campaign={data.campaign} stats={stats} />
      <OrganicCampaignDetailsTabs
        campaign={data.campaign}
        retailerAccountSlug={retailerAccountSlug}
        brandSlug={brandSlug}
        campaignId={campaignId}
        value={channel}
      />
      <OrganicCampaignDetailsList
        campaign={data.campaign}
        channel={channel}
        stats={stats}
      />
    </Box>
  )
}

export default OrganicCampaignDetails
